import { z } from "zod";
export const appInfoSchema = z.object({
    name: z.literal("Sage"),
    phase: z.literal("mvp-skeleton")
});
export const appInfo = {
    name: "Sage",
    phase: "mvp-skeleton"
};
export const hashAlgorithmSchema = z.enum(["md5", "sha1", "sha256"]);
const normalizedHashSchema = (algorithm, length) => z.string().transform((value, context) => {
    if (!new RegExp(`^[a-fA-F0-9]{${length}}$`).test(value)) {
        context.addIssue({
            code: "custom",
            message: `Expected a raw ${algorithm} hash`
        });
        return z.NEVER;
    }
    return value.toLowerCase();
});
export const md5HashSchema = z
    .string()
    .pipe(normalizedHashSchema("MD5", 32));
export const sha1HashSchema = z
    .string()
    .pipe(normalizedHashSchema("SHA1", 40));
export const sha256HashSchema = z
    .string()
    .pipe(normalizedHashSchema("SHA256", 64));
export const hashIndicatorSchema = z.discriminatedUnion("algorithm", [
    z.object({ algorithm: z.literal("md5"), value: md5HashSchema }).strict(),
    z.object({ algorithm: z.literal("sha1"), value: sha1HashSchema }).strict(),
    z.object({ algorithm: z.literal("sha256"), value: sha256HashSchema }).strict()
]);
export function parseMd5Hash(input) {
    return md5HashSchema.parse(input);
}
export function parseSha1Hash(input) {
    return sha1HashSchema.parse(input);
}
export function parseSha256Hash(input) {
    return sha256HashSchema.parse(input);
}
const rawHashSchema = z
    .string()
    .transform((value, context) => {
    if (!/^(?:[a-fA-F0-9]{32}|[a-fA-F0-9]{40}|[a-fA-F0-9]{64})$/.test(value)) {
        context.addIssue({
            code: "custom",
            message: "Expected a raw MD5, SHA1, or SHA256 hash"
        });
        return z.NEVER;
    }
    return value.toLowerCase();
});
export const lookupHashSchema = rawHashSchema;
export function parseHashIndicator(input) {
    const value = lookupHashSchema.parse(input);
    if (value.length === 32) {
        return { algorithm: "md5", value };
    }
    if (value.length === 40) {
        return { algorithm: "sha1", value };
    }
    return { algorithm: "sha256", value };
}
export const createHashIndicator = parseHashIndicator;
export const lookupRequestSchema = z
    .object({
    hash: lookupHashSchema
})
    .strict();
export const reputationVerdictSchema = z.enum([
    "clean",
    "unknown",
    "suspicious",
    "malicious",
    "critical"
]);
export const confidenceScoreSchema = z.number().int().min(0).max(100);
export const quotaStateSchema = z.enum([
    "under_quota",
    "near_quota",
    "quota_exceeded",
    "upgrade_suggested",
    "billing_unavailable",
    "payment_failed",
    "feature_unavailable",
    "enterprise_contact_required",
    "sandbox_not_available_yet"
]);
export const quotaSchema = z
    .object({
    feature: z.string().min(1),
    state: quotaStateSchema,
    used: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    period: z.enum(["day", "month"]),
    resetsAt: z.string().datetime().optional()
})
    .strict();
export const reputationSourceResultSchema = z
    .object({
    name: z.string().min(1),
    verdict: reputationVerdictSchema,
    confidence: confidenceScoreSchema,
    detections: z.number().int().nonnegative(),
    totalEngines: z.number().int().nonnegative(),
    tags: z.array(z.string().min(1)),
    references: z.array(z.string().url()),
    firstSeen: z.string().datetime().optional(),
    lastSeen: z.string().datetime().optional(),
    lastUpdated: z.string().datetime()
})
    .strict();
export const reputationReportSchema = z
    .object({
    hash: hashIndicatorSchema,
    verdict: reputationVerdictSchema,
    confidence: confidenceScoreSchema,
    detections: z.number().int().nonnegative(),
    tags: z.array(z.string().min(1)),
    metadata: z.record(z.string(), z.unknown()),
    references: z.array(z.string().url()),
    sources: z.array(reputationSourceResultSchema),
    firstSeen: z.string().datetime().optional(),
    lastSeen: z.string().datetime().optional(),
    lookedUpAt: z.string().datetime()
})
    .strict();
export const apiErrorSchema = z
    .object({
    code: z.string().min(1),
    message: z.string().min(1),
    details: z.unknown().optional(),
    requestId: z.string().min(1)
})
    .strict();
export function apiSuccessEnvelopeSchema(dataSchema) {
    return z
        .object({
        ok: z.literal(true),
        data: dataSchema
    })
        .strict();
}
export const apiErrorEnvelopeSchema = z
    .object({
    ok: z.literal(false),
    error: apiErrorSchema
})
    .strict();
export const apiResponseEnvelopeSchema = (dataSchema) => z.discriminatedUnion("ok", [apiSuccessEnvelopeSchema(dataSchema), apiErrorEnvelopeSchema]);
// ---------------------------------------------------------------------------
// Explore / Samples
// ---------------------------------------------------------------------------
export const exploreSampleSchema = z
    .object({
    id: z.string(),
    sha256: z.string().length(64),
    fileName: z.string().nullable(),
    fileType: z.string().nullable(),
    fileSize: z.number().int().nullable(),
    verdict: reputationVerdictSchema,
    tags: z.array(z.string()),
    firstSeen: z.string().nullable(),
    lastScanned: z.string().nullable(),
    createdAt: z.string()
})
    .strict();
export const exploreSamplesResponseSchema = z
    .object({
    samples: z.array(exploreSampleSchema),
    pagination: z.object({
        page: z.number().int().positive(),
        limit: z.number().int().positive().max(100),
        total: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative()
    })
})
    .strict();
export const sampleUploadResponseSchema = z
    .object({
    sample: exploreSampleSchema,
    alreadyExists: z.boolean()
})
    .strict();
